<script lang="ts" setup>
import { computed, ref, watch } from "vue";

import { useAppStore } from "@/store/app.store";
import { Role, RoleMap } from "@/constants/navigation";

import type { DispatcherSelectValue } from "@/types/dispatcher-select";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const model = defineModel<DispatcherSelectValue>();

const props = withDefaults(
  defineProps<{
    id?: string;
    label?: string;
    required?: boolean;
    multiple?: boolean;
    displayedSelect?: number;
    showRoles?: Role[];
    clearOnSelect?: boolean;
  }>(),
  {
    required: false,
    multiple: false,
    displayedSelect: 1,
    showRoles: () => [],
    clearOnSelect: true,
  },
);

const emit = defineEmits<{
  (e: "search", value: DispatcherSelectValue): void;
}>();

const appStore = useAppStore();

const hasValueChanged = ref(false);

const isLoading = computed(() => !appStore.dispatchers || appStore.dispatchers.length === 0);

const value = computed<DispatcherSelectValue>({
  get() {
    return isLoading.value ? "Loading..." : model.value;
  },
  set(newValue) {
    model.value = newValue;
  },
});

const dispatchersToShow = computed(() => {
  const sorted = [...appStore.dispatchers].sort((a, b) => {
    const hasABooking = a.roles.includes(Role.booking) ? 0 : 1;
    const hasBBooking = b.roles.includes(Role.booking) ? 0 : 1;

    return hasABooking - hasBBooking;
  });

  if (!props.showRoles.length) {
    return sorted;
  }

  return sorted.filter((dispatcher) => {
    return props.showRoles.some((role) => dispatcher.roles.includes(role));
  });
});

watch(value, (newValue, oldValue) => {
  hasValueChanged.value = JSON.stringify(newValue) !== JSON.stringify(oldValue);
});

function onBlur() {
  if (hasValueChanged.value) {
    emit("search", value.value);

    hasValueChanged.value = false;
  }
}
</script>

<template>
  <v-autocomplete
    :id="id"
    v-model="value"
    :class="{ required }"
    :clear-on-select="clearOnSelect"
    :clearable="true"
    :items="dispatchersToShow"
    :label="label || t('dispatchers.dispatcher')"
    :loading="isLoading"
    :multiple="multiple"
    autocomplete="off"
    class="dispatcher-select"
    color="primary"
    density="compact"
    hide-details="auto"
    item-title="name"
    item-value="id"
    variant="outlined"
    @blur="onBlur"
  >
    <template #item="{ item, props: itemProps }">
      <v-list-item :title="item.raw.name || ''" v-bind="itemProps">
        <div class="d-flex align-center">
          <div v-for="(role, index) of item.raw.roles" :key="role" class="text-grey-darken-1 mr-2px">
            <span>{{ RoleMap[role] }}</span>
            <span v-if="index !== item.raw.roles.length - 1">,</span>
          </div>
        </div>
      </v-list-item>
    </template>

    <template #selection="{ item, index }">
      <span
        v-if="displayedSelect && displayedSelect > index"
        class="d-flex align-center white-space-nowrap overflow-hidden text-overflow-ellipsis"
      >
        <span class="text-grey-darken-4 mr-2px">{{ item.raw.name }}</span>

        <span class="text-grey-darken-2">
          <span v-if="Array.isArray(value) && index !== value.length - 1">,</span>
        </span>
      </span>

      <span v-if="index === displayedSelect">...</span>
    </template>
  </v-autocomplete>
</template>

<style lang="scss" scoped>
.dispatcher-select {
  &:deep(.v-field__input) {
    flex-wrap: nowrap;
  }
}
</style>
